import { useFetch } from "~vue/api";
import type { CreateShareInvitationRequest, ShareInvitationCreated } from "~vue/api/generated/collab-profiles";
import { type ProfileShareSource, type ProfileShareTarget, withShareAttribution } from "~vue/CollaborationProfile/profileShareAttribution";
import { useTimedShow } from "~vue/composables/useTimedShow";
import { ACCESS } from "~vue/utils/constants";
import { logError } from "~vue/utils/logError";
import { computed, type ComputedRef, ref } from "vue";

type ProfileShareLinkArgs = {
    profileId: ComputedRef<string | undefined>;
    activeShareUrl?: ComputedRef<string | undefined>;
    // Absolute URL to the user's own profile, used for ORGANIZATION access (which has no
    // per-invite link). Falls back to the current page URL when omitted — correct when
    // sharing from the profile page itself, but callers on other pages should pass this.
    ownProfileUrl?: ComputedRef<string | undefined>;
    // Tags copied links so the recipient's open can be attributed to this surface.
    shareSource?: ProfileShareSource;
};

export const useProfileShareLink = ({ profileId, activeShareUrl, ownProfileUrl, shareSource }: ProfileShareLinkArgs) => {
    const generateUrl = computed(() => (profileId.value ? `/v1/profiles/${profileId.value}/shared-invitations` : ""));
    // Optional POST body: undefined mints the anonymous "copy link"; set it to target a
    // specific colleague. useFetch resolves it (via toValue) at execute() time, so callers set
    // it immediately before calling executeGenerateLink().
    const invitePayload = ref<CreateShareInvitationRequest | undefined>(undefined);
    const {
        isFetching: isGeneratingLink,
        data: generatedLink,
        error: shareLinkError,
        execute: executeGenerateLink,
    } = useFetch(generateUrl, { immediate: false }).post(invitePayload).json<ShareInvitationCreated>();

    const ensureShareLink = async (accessLevel: string): Promise<string | null> => {
        if (accessLevel === ACCESS.PRIVATE) {
            shareLinkError.value = "You do not have sharing access set.";
            return null;
        }
        if (accessLevel === ACCESS.ORGANIZATION) {
            return ownProfileUrl?.value ?? globalThis.location.href;
        }
        if (activeShareUrl?.value) {
            return activeShareUrl.value;
        }
        if (generatedLink.value?.share_url) {
            return generatedLink.value.share_url;
        }
        invitePayload.value = undefined; // anonymous "copy link" — no targeted body
        await executeGenerateLink();
        return generatedLink.value?.share_url ?? null;
    };

    // Create (or return the active) targeted invitation of this profile for a specific
    // colleague, returning its share URL. The server resolves the invitee's email from invited_user_id.
    const createTargetedInvitation = async (invitedUserId: string): Promise<string | null> => {
        if (!generateUrl.value) {
            return null;
        }
        shareLinkError.value = null;
        invitePayload.value = { invited_user_id: invitedUserId };
        await executeGenerateLink();
        return generatedLink.value?.share_url ?? null;
    };

    const { isShowing: isCopied, show } = useTimedShow({ initialState: false, duration: 2000 });

    const handleCopyLink = async (accessLevel: string, cb?: () => void, message?: string, target: ProfileShareTarget = "link"): Promise<boolean> => {
        shareLinkError.value = null;
        const shareUrl = await ensureShareLink(accessLevel);
        if (!shareUrl) {
            return false;
        }
        const url = shareSource ? withShareAttribution(shareUrl, shareSource, target) : shareUrl;
        try {
            await navigator.clipboard.writeText(message ? `${message}\n\n${url}` : url);
            show();
            cb?.();
            return true;
        } catch {
            logError("Failed to copy link to clipboard");
            shareLinkError.value = "Failed to copy link to clipboard. Please try copying the URL manually.";
            return false;
        }
    };

    return {
        isGeneratingLink,
        shareLinkError,
        ensureShareLink,
        createTargetedInvitation,
        handleCopyLink,
        isCopied,
    };
};
